import React, { useState } from 'react';
import { Book, ExternalLink, ChevronDown, ChevronUp, Check, AlertCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';

const APISetupGuide = ({ platform, onComplete }) => {
  const [expandedSection, setExpandedSection] = useState(null);

  const platformGuides = {
    twitter: {
      name: 'Twitter/X API',
      icon: '𝕏',
      steps: [
        {
          title: 'Create Developer Account',
          description: 'Sign up for a Twitter Developer account',
          link: 'https://developer.twitter.com/en/portal/dashboard',
          details: [
            'Go to Twitter Developer Portal',
            'Sign in with your Twitter account',
            'Complete the developer application form',
            'Wait for approval (usually instant for basic access)'
          ]
        },
        {
          title: 'Create New App',
          description: 'Set up your application in the Developer Portal',
          details: [
            'Click "Create App" in the dashboard',
            'Fill in app name and description',
            'Set app permissions (Read and Write recommended)',
            'Note down your App ID'
          ]
        },
        {
          title: 'Generate API Keys',
          description: 'Create your API credentials',
          credentials: [
            'API Key (Consumer Key)',
            'API Secret (Consumer Secret)',
            'Access Token',
            'Access Token Secret',
            'Bearer Token'
          ],
          details: [
            'Navigate to "Keys and tokens" tab',
            'Generate API Key and Secret',
            'Generate Access Token and Secret',
            'Generate Bearer Token',
            'Copy all credentials securely'
          ]
        },
        {
          title: 'Configure Rate Limits',
          description: 'Understanding API limits',
          details: [
            'Free tier: 500,000 tweets/month',
            'Basic tier: 10,000 tweets/month ($100/month)',
            'Pro tier: 1M tweets/month ($5,000/month)',
            'Monitor usage in developer dashboard'
          ]
        }
      ],
      requiredFields: [
        { name: 'apiKey', label: 'API Key', type: 'text' },
        { name: 'apiSecret', label: 'API Secret', type: 'password' },
        { name: 'accessToken', label: 'Access Token', type: 'text' },
        { name: 'accessTokenSecret', label: 'Access Token Secret', type: 'password' },
        { name: 'bearerToken', label: 'Bearer Token', type: 'password' }
      ]
    },
    facebook: {
      name: 'Facebook Graph API',
      icon: '📘',
      steps: [
        {
          title: 'Create Meta Developer Account',
          description: 'Register as a Meta developer',
          link: 'https://developers.facebook.com/',
          details: [
            'Visit Meta for Developers',
            'Click "Get Started"',
            'Complete registration process',
            'Verify your account'
          ]
        },
        {
          title: 'Create New App',
          description: 'Set up Facebook application',
          details: [
            'Go to "My Apps"',
            'Click "Create App"',
            'Select "Business" type',
            'Fill in app details'
          ]
        },
        {
          title: 'Add Facebook Page',
          description: 'Connect your Facebook Page',
          details: [
            'Navigate to App Dashboard',
            'Add "Facebook Login" product',
            'Configure Page permissions',
            'Get Page Access Token'
          ]
        },
        {
          title: 'Generate Access Token',
          description: 'Create long-lived access token',
          credentials: [
            'Page Access Token',
            'Page ID'
          ],
          details: [
            'Use Graph API Explorer',
            'Select your Page',
            'Generate User Access Token',
            'Exchange for long-lived token',
            'Store token securely'
          ]
        }
      ],
      requiredFields: [
        { name: 'accessToken', label: 'Page Access Token', type: 'password' },
        { name: 'pageId', label: 'Page ID', type: 'text' }
      ]
    },
    instagram: {
      name: 'Instagram Basic Display API',
      icon: '📷',
      steps: [
        {
          title: 'Create Meta Developer Account',
          description: 'Same as Facebook setup',
          link: 'https://developers.facebook.com/',
          details: [
            'Use existing Meta developer account',
            'Or create new account if needed'
          ]
        },
        {
          title: 'Create Instagram Business Account',
          description: 'Convert to business account',
          details: [
            'Open Instagram app',
            'Go to Settings > Account',
            'Switch to Professional Account',
            'Choose Business category'
          ]
        },
        {
          title: 'Connect to Facebook Page',
          description: 'Link Instagram to Facebook',
          details: [
            'In Instagram settings',
            'Go to "Linked Accounts"',
            'Connect to Facebook Page',
            'Authorize permissions'
          ]
        },
        {
          title: 'Get Instagram Business ID',
          description: 'Retrieve account credentials',
          credentials: [
            'Instagram Business Account ID',
            'Access Token'
          ],
          details: [
            'Use Graph API Explorer',
            'Query: /me/accounts',
            'Get connected Instagram account',
            'Note Instagram Business ID',
            'Generate access token'
          ]
        }
      ],
      requiredFields: [
        { name: 'accessToken', label: 'Access Token', type: 'password' },
        { name: 'accountId', label: 'Instagram Business Account ID', type: 'text' }
      ]
    },
    linkedin: {
      name: 'LinkedIn API',
      icon: '💼',
      steps: [
        {
          title: 'Create LinkedIn App',
          description: 'Register application with LinkedIn',
          link: 'https://www.linkedin.com/developers/apps',
          details: [
            'Go to LinkedIn Developers',
            'Click "Create app"',
            'Fill in app details',
            'Associate with LinkedIn Page/Company'
          ]
        },
        {
          title: 'Request API Access',
          description: 'Apply for Marketing Developer Platform',
          details: [
            'Navigate to "Products" tab',
            'Select "Marketing Developer Platform"',
            'Submit application',
            'Wait for LinkedIn approval (2-4 weeks)'
          ]
        },
        {
          title: 'Generate Access Token',
          description: 'Create OAuth 2.0 credentials',
          credentials: [
            'Client ID',
            'Client Secret',
            'Access Token',
            'Organization ID'
          ],
          details: [
            'Go to "Auth" tab',
            'Note Client ID and Secret',
            'Use OAuth 2.0 flow',
            'Get organization ID from company page'
          ]
        }
      ],
      requiredFields: [
        { name: 'accessToken', label: 'Access Token', type: 'password' },
        { name: 'organizationId', label: 'Organization ID', type: 'text' }
      ]
    },
    reddit: {
      name: 'Reddit API',
      icon: '🤖',
      steps: [
        {
          title: 'Create Reddit Account',
          description: 'Register or use existing account',
          link: 'https://www.reddit.com/',
          details: [
            'Create Reddit account if needed',
            'Verify email address',
            'Build account karma (recommended)'
          ]
        },
        {
          title: 'Create App',
          description: 'Register application with Reddit',
          link: 'https://www.reddit.com/prefs/apps',
          details: [
            'Go to App Preferences',
            'Scroll to "Developed Applications"',
            'Click "Create App"',
            'Select "script" type',
            'Fill in app details'
          ]
        },
        {
          title: 'Get Credentials',
          description: 'Copy API credentials',
          credentials: [
            'Client ID',
            'Client Secret',
            'Username',
            'Password'
          ],
          details: [
            'Note Client ID (under app name)',
            'Copy Client Secret',
            'Use your Reddit username',
            'Use your Reddit password',
            'Keep credentials secure'
          ]
        }
      ],
      requiredFields: [
        { name: 'clientId', label: 'Client ID', type: 'text' },
        { name: 'clientSecret', label: 'Client Secret', type: 'password' },
        { name: 'username', label: 'Reddit Username', type: 'text' },
        { name: 'password', label: 'Reddit Password', type: 'password' }
      ]
    },
    youtube: {
      name: 'YouTube Data API',
      icon: '📺',
      steps: [
        {
          title: 'Create Google Cloud Project',
          description: 'Set up Google Cloud Console project',
          link: 'https://console.cloud.google.com/',
          details: [
            'Go to Google Cloud Console',
            'Create new project',
            'Enable billing (free tier available)',
            'Note project ID'
          ]
        },
        {
          title: 'Enable YouTube Data API',
          description: 'Activate API for your project',
          details: [
            'Navigate to "APIs & Services"',
            'Click "Enable APIs and Services"',
            'Search "YouTube Data API v3"',
            'Click "Enable"'
          ]
        },
        {
          title: 'Create API Key',
          description: 'Generate credentials',
          credentials: [
            'API Key'
          ],
          details: [
            'Go to "Credentials"',
            'Click "Create Credentials"',
            'Select "API Key"',
            'Restrict key to YouTube Data API',
            'Copy API key'
          ]
        },
        {
          title: 'Set Quota Limits',
          description: 'Monitor API usage',
          details: [
            'Free tier: 10,000 units/day',
            'Each search: 100 units',
            'Monitor in Cloud Console',
            'Request quota increase if needed'
          ]
        }
      ],
      requiredFields: [
        { name: 'apiKey', label: 'YouTube API Key', type: 'password' }
      ]
    },
    google_reviews: {
      name: 'Google Reviews (Places API)',
      icon: '🗺️',
      steps: [
        {
          title: 'Create Google Cloud Project',
          description: 'Same as YouTube setup',
          link: 'https://console.cloud.google.com/',
          details: [
            'Use existing project or create new',
            'Enable billing'
          ]
        },
        {
          title: 'Enable Places API',
          description: 'Activate Google Places API',
          details: [
            'Go to "APIs & Services"',
            'Enable "Places API"',
            'Enable "Maps JavaScript API"'
          ]
        },
        {
          title: 'Get Place ID',
          description: 'Find your business Place ID',
          link: 'https://developers.google.com/maps/documentation/places/web-service/place-id',
          details: [
            'Use Place ID Finder tool',
            'Search for your business',
            'Copy Place ID',
            'Store securely'
          ]
        },
        {
          title: 'Create API Key',
          description: 'Generate credentials',
          credentials: [
            'API Key',
            'Place ID'
          ],
          details: [
            'Create API key in Credentials',
            'Restrict to Places API',
            'Set IP restrictions (recommended)'
          ]
        }
      ],
      requiredFields: [
        { name: 'apiKey', label: 'Google Places API Key', type: 'password' },
        { name: 'placeId', label: 'Place ID', type: 'text' }
      ]
    },
    yelp: {
      name: 'Yelp Fusion API',
      icon: '⭐',
      steps: [
        {
          title: 'Create Yelp Account',
          description: 'Register Yelp business account',
          link: 'https://www.yelp.com/',
          details: [
            'Create Yelp account',
            'Claim your business listing',
            'Verify ownership'
          ]
        },
        {
          title: 'Register Developer Account',
          description: 'Sign up for API access',
          link: 'https://www.yelp.com/developers/v3/manage_app',
          details: [
            'Go to Yelp Developers',
            'Click "Create New App"',
            'Agree to terms',
            'Fill in app details'
          ]
        },
        {
          title: 'Get API Key',
          description: 'Retrieve credentials',
          credentials: [
            'API Key',
            'Business ID'
          ],
          details: [
            'Copy API Key from dashboard',
            'Find Business ID in URL',
            'Keep key secure'
          ]
        },
        {
          title: 'Understand Limits',
          description: 'API rate limits',
          details: [
            'Free tier: 5,000 calls/day',
            'Rate limit: 5 QPS',
            'Monitor usage in dashboard'
          ]
        }
      ],
      requiredFields: [
        { name: 'apiKey', label: 'Yelp API Key', type: 'password' },
        { name: 'businessId', label: 'Business ID', type: 'text' }
      ]
    },
    trustpilot: {
      name: 'Trustpilot API',
      icon: '⭐',
      steps: [
        {
          title: 'Create Trustpilot Business Account',
          description: 'Register business profile',
          link: 'https://www.trustpilot.com/',
          details: [
            'Create business account',
            'Verify business ownership',
            'Complete profile setup'
          ]
        },
        {
          title: 'Apply for API Access',
          description: 'Request developer credentials',
          link: 'https://developers.trustpilot.com/',
          details: [
            'Go to Trustpilot Developers',
            'Submit API access request',
            'Wait for approval',
            'Receive API key via email'
          ]
        },
        {
          title: 'Get Business Unit ID',
          description: 'Find your business identifier',
          credentials: [
            'API Key',
            'Business Unit ID'
          ],
          details: [
            'Login to Trustpilot',
            'Go to Business Settings',
            'Copy Business Unit ID',
            'Use API key from email'
          ]
        }
      ],
      requiredFields: [
        { name: 'apiKey', label: 'Trustpilot API Key', type: 'password' },
        { name: 'businessUnitId', label: 'Business Unit ID', type: 'text' }
      ]
    },
    news_api: {
      name: 'News API',
      icon: '📰',
      steps: [
        {
          title: 'Create News API Account',
          description: 'Register for API access',
          link: 'https://newsapi.org/register',
          details: [
            'Go to NewsAPI.org',
            'Click "Get API Key"',
            'Fill in registration form',
            'Verify email address'
          ]
        },
        {
          title: 'Choose Plan',
          description: 'Select subscription tier',
          details: [
            'Free tier: 100 requests/day',
            'Developer: 250/day ($49/month)',
            'Professional: 10,000/day ($449/month)',
            'Enterprise: Custom pricing'
          ]
        },
        {
          title: 'Get API Key',
          description: 'Copy credentials',
          credentials: [
            'API Key'
          ],
          details: [
            'API key shown after registration',
            'Access from account dashboard',
            'Keep key secure',
            'Monitor usage'
          ]
        }
      ],
      requiredFields: [
        { name: 'apiKey', label: 'News API Key', type: 'password' }
      ]
    },
    app_store: {
      name: 'App Store Reviews',
      icon: '🍎',
      steps: [
        {
          title: 'Get App Store App ID',
          description: 'Find your application ID',
          details: [
            'Go to App Store Connect',
            'Select your app',
            'Copy App ID from URL',
            'Or use iTunes lookup'
          ]
        },
        {
          title: 'Use RSS Feed',
          description: 'Access public reviews',
          credentials: [
            'App ID',
            'Country Code (optional)'
          ],
          details: [
            'App Store reviews are public',
            'No API key required',
            'Use RSS feed endpoint',
            'Specify country for region-specific reviews'
          ]
        }
      ],
      requiredFields: [
        { name: 'appId', label: 'App Store App ID', type: 'text' }
      ]
    },
    google_play: {
      name: 'Google Play Reviews',
      icon: '📱',
      steps: [
        {
          title: 'Create Google Cloud Project',
          description: 'Setup Google Cloud Console',
          link: 'https://console.cloud.google.com/',
          details: [
            'Create or use existing project',
            'Enable Google Play Developer API',
            'Set up service account'
          ]
        },
        {
          title: 'Link to Play Console',
          description: 'Connect to developer account',
          link: 'https://play.google.com/console/',
          details: [
            'Go to Play Console',
            'Settings > API access',
            'Link Cloud project',
            'Grant permissions'
          ]
        },
        {
          title: 'Get Package Name',
          description: 'Find application identifier',
          credentials: [
            'Package Name',
            'Service Account Credentials'
          ],
          details: [
            'Copy package name from Play Console',
            'Download service account JSON',
            'Store credentials securely'
          ]
        }
      ],
      requiredFields: [
        { name: 'packageName', label: 'Package Name', type: 'text' }
      ]
    }
  };

  const guide = platformGuides?.[platform];

  if (!guide) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <AlertCircle className="w-6 h-6 text-gray-400 mb-2" />
        <p className="text-gray-600">Setup guide not available for this platform.</p>
      </div>
    );
  }

  const toggleSection = (index) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-4xl">{guide?.icon}</span>
          <div>
            <h3 className="text-xl font-bold text-gray-900">{guide?.name}</h3>
            <p className="text-sm text-gray-600">Step-by-step integration guide</p>
          </div>
        </div>
        <Book className="w-6 h-6 text-indigo-600" />
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {guide?.steps?.map((step, index) => (
          <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Step Header */}
            <button
              onClick={() => toggleSection(index)}
              className="w-full px-6 py-4 bg-white hover:bg-gray-50 flex items-center justify-between transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                  ${expandedSection === index ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}
                `}>
                  {index + 1}
                </div>
                <div className="text-left">
                  <h4 className="font-semibold text-gray-900">{step?.title}</h4>
                  <p className="text-sm text-gray-600">{step?.description}</p>
                </div>
              </div>
              {expandedSection === index ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {/* Step Content */}
            {expandedSection === index && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                {step?.link && (
                  <a
                    href={step?.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 mb-4 font-medium"
                  >
                    Open Developer Portal
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}

                {step?.credentials && (
                  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-semibold text-amber-900 mb-2">Required Credentials:</p>
                    <ul className="space-y-1">
                      {step?.credentials?.map((cred, idx) => (
                        <li key={idx} className="text-sm text-amber-800 flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          {cred}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <ul className="space-y-2">
                  {step?.details?.map((detail, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-700">
                      <span className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-semibold text-indigo-700">{idx + 1}</span>
                      </span>
                      <span className="text-sm">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Required Fields Summary */}
      <div className="mt-6 p-6 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          Required API Fields
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {guide?.requiredFields?.map((field, index) => (
            <div key={index} className="flex items-center gap-2 text-sm text-blue-800">
              <Check className="w-4 h-4 text-blue-600" />
              <span className="font-medium">{field?.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action Button */}
      <div className="flex justify-end">
        <Button
          onClick={onComplete}
          className="flex items-center gap-2"
        >
          <Check className="w-4 h-4" />
          I've Completed Setup
        </Button>
      </div>
    </div>
  );
};

export default APISetupGuide;